import { generatePath } from 'react-router';
import store from '@Store';
import omit from 'lodash/omit';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';
import { updateDialog } from '@Containers/Dialogs/DialogsActions';
import MessageBroker from '@Services/MessageBroker';
import RequestUrls from '@Constants/RequestUrlsConstants';
import Auth from '@Services/Auth';
import Catalyst from '@Services/Catalyst';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import {
  typeOfRemoteAccessActivationSuccessDialog,
  typeOfRemoteAccessActivationErrorDialog,
  errorTypeEnforcedNoReg,
} from './RemoteAccessDialogConstants';

export function sendEmailForEligibility(email) {
  return MessageBroker.send(RequestUrls.remoteAccessEligibility, { email });
}

export function activateEmailForDepartmentId(email, departmentId) {
  const token = Auth.getAccessToken();
  return MessageBroker.send(
    RequestUrls.remoteAccessActivate,
    {
      email,
      choosenDepartmentId: departmentId,
    },
    token && {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
}

// Go to login page
function _gotoLogin() {
  history.push({
    pathname: generatePath(paths.LOGIN),
  });
}

// Go to registration page
function _gotoRegister() {
  history.push({
    pathname: paths.PRIVATE_REGISTER,
  });
}

// Go to landing page
function _gotoLanding() {
  return Auth.goToLandingPage();
}

// If there was a Reaxys session - kill it
async function _killSession() {
  if (Auth.isLoggedIn() && Auth.isReaxysAllowed()) {
    await Auth.logout(true);
  }
}

// Show activation success dialog
function _showSuccessDialog(response) {
  store.dispatch(
    updateDialog({
      id: typeOfRemoteAccessActivationSuccessDialog,
      dialogType: typeOfRemoteAccessActivationSuccessDialog,
      message: getValueByNs(response, 'notifications.ENFORCED_LOGIN_MESSAGE'),
    })
  );
}

// Show activation error dialog
function _showErrorDialog(error, errorTitle) {
  const errorMessage = getValueByNs(error, 'message');
  Catalyst.trackError({
    message: `${errorTitle} ${errorMessage}`,
    component: 'RemoteAccess',
  });
  store.dispatch(
    updateDialog({
      id: typeOfRemoteAccessActivationErrorDialog,
      dialogType: typeOfRemoteAccessActivationErrorDialog,
      message: getValueByNs(error, 'message'),
    })
  );
}

// eslint-disable-next-line camelcase
export async function activationFromEmailLink({ selfManRaToken, grant_type }) {
  try {
    // Send pure oauth/token request, without additional handlers from Auth service
    const response = await Auth._sendOauthTokenRequest(
      {
        selfManRaToken,
        // eslint-disable-next-line camelcase
        grant_type,
      },
      {
        ignoreIntercepts: true,
      }
    );

    Logger.info('RemoteAccess - activation success');
    await _killSession();
    _showSuccessDialog(response);

    // Handle new access token response
    // remove notifications from login page, it will be shown in dialog
    Auth._accessTokenHandler({ response: omit(response, 'notifications') });

    // If new temp access token has registration permissions - go to register,
    // or go to login otherwise
    if (Auth.isRegistrationEnabled()) {
      _gotoRegister();
    } else {
      _gotoLogin();
    }
  } catch (error) {
    const errorType = getValueByNs(error, 'type');

    if (errorType === errorTypeEnforcedNoReg) {
      Logger.info('RemoteAccess - activation success, enforced login');
      await _killSession();
      _showSuccessDialog(error);
      _gotoLogin();
    } else {
      const errorTitle = 'RemoteAccess - activation fail';
      Logger.error(errorTitle, error);
      _showErrorDialog(error, errorTitle);
      _gotoLanding();
    }
  }
}
